import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';
import Swal from 'sweetalert2';
import { BASE_URL } from '../config/socket';

function Settings() {
  const [paymentMode, setPaymentMode] = useState('manual'); // 'manual', 'promptpay'
  const [formData, setFormData] = useState({
    store_name: '',
    store_address: '',
    store_phone: '',
    store_email: '',
    tax_id: '',
    slogan: '',
    footer_text: '',
    points_earn_baht: 100,
    points_earn_points: 1,
    redemption_points: 100,
    redemption_value: 10,
    qr_mode: 'manual',
    promptpay_type: 'phone',
    promptpay_id: ''
  });
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [qrCodePreview, setQrCodePreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setFormData({
        store_name: response.data.store_name || '',
        store_address: response.data.store_address || '',
        store_phone: response.data.store_phone || '',
        store_email: response.data.store_email || '',
        tax_id: response.data.tax_id || '',
        slogan: response.data.slogan || '',
        footer_text: response.data.footer_text || '',
        points_earn_baht: response.data.points_earn_baht || 100,
        points_earn_points: response.data.points_earn_points || 1,
        redemption_points: response.data.redemption_points || 100,
        redemption_value: response.data.redemption_value || 10,
        qr_mode: response.data.qr_mode || 'manual',
        promptpay_type: response.data.promptpay_type || 'phone',
        promptpay_id: response.data.promptpay_id || ''
      });

      // Determine payment mode from loaded data
      if (response.data.qr_mode === 'manual') {
        setPaymentMode('manual');
      } else if (response.data.promptpay_id) {
        setPaymentMode('promptpay');
      } else {
        setPaymentMode('manual');
      }

      if (response.data.payment_qr_code) {
        setQrCodePreview(`${BASE_URL.replace('/api', '')}${response.data.payment_qr_code}`);
      }
      if (response.data.store_logo) {
        setLogoPreview(`${BASE_URL.replace('/api', '')}${response.data.store_logo}`);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleInfoUpdate = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      await settingsAPI.update(formData);
      await fetchSettings();

      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ!',
        text: 'บันทึกการตั้งค่าเรียบร้อยแล้ว',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#10b981'
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      Swal.fire({
        icon: 'error',
        title: 'ผิดพลาด!',
        text: 'เกิดข้อผิดพลาดในการบันทึก: ' + (error.response?.data?.error || 'กรุณาลองใหม่อีกครั้ง'),
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleQrCodeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQrCodeFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setQrCodePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleQrCodeUpload = async (e) => {
    e.preventDefault();

    if (!qrCodeFile) {
      setMessage({ type: 'error', text: 'กรุณาเลือกรูป QR Code' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('qrcode', qrCodeFile);

      await settingsAPI.uploadQRCode(formData);

      setMessage({ type: 'success', text: 'อัปโหลด QR Code สำเร็จ!' });
      setQrCodeFile(null);
      fetchSettings();
    } catch (error) {
      console.error('Error uploading QR code:', error);
      setMessage({
        type: 'error',
        text: 'เกิดข้อผิดพลาดในการอัปโหลด: ' + (error.response?.data?.error || 'ข้อผิดพลาดไม่ทราบสาเหตุ')
      });
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    e.preventDefault();

    if (!logoFile) {
      setMessage({ type: 'error', text: 'กรุณาเลือกรูปโลโก้' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      await settingsAPI.uploadLogo(formData);

      setMessage({ type: 'success', text: 'อัปโหลดโลโก้สำเร็จ!' });
      setLogoFile(null);
      fetchSettings();
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage({
        type: 'error',
        text: 'เกิดข้อผิดพลาดในการอัปโหลด: ' + (error.response?.data?.error || 'ข้อผิดพลาดไม่ทราบสาเหตุ')
      });
    } finally {
      setUploading(false);
    }
  };

  // Data management functions
  const handleDeleteSales = async () => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบข้อมูลการขาย',
      html: `
        <div class="text-left">
          <p class="mb-2 text-red-600 font-bold">⚠️ คำเตือน: การดำเนินการนี้ไม่สามารถย้อนกลับได้!</p>
          <p class="mb-2">ข้อมูลที่จะถูกลบ:</p>
          <ul class="list-disc ml-6 text-sm">
            <li>ข้อมูลการขายทั้งหมด</li>
            <li>รายการสินค้าในบิล</li>
            <li>ประวัติการเคลื่อนไหวสต็อก (เฉพาะการขาย)</li>
          </ul>
          <p class="mt-3 text-sm text-gray-600">กรุณาพิมพ์ <strong class="text-red-600">DELETE</strong> เพื่อยืนยัน</p>
        </div>
        <input id="confirm-text" class="swal2-input" placeholder="พิมพ์ DELETE">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ลบข้อมูล',
      cancelButtonText: 'ยกเลิก',
      preConfirm: () => {
        const value = document.getElementById('confirm-text').value;
        if (value !== 'DELETE') {
          Swal.showValidationMessage('กรุณาพิมพ์ DELETE เพื่อยืนยัน');
          return false;
        }
        return true;
      }
    });

    if (result.isConfirmed) {
      try {
        await settingsAPI.deleteSales();
        Swal.fire('สำเร็จ!', 'ลบข้อมูลการขายทั้งหมดเรียบร้อยแล้ว', 'success');
      } catch (error) {
        Swal.fire('ผิดพลาด!', error.response?.data?.error || 'เกิดข้อผิดพลาด', 'error');
      }
    }
  };

  const handleResetStock = async () => {
    const result = await Swal.fire({
      title: 'ยืนยันการเคลียร์สต็อก',
      html: `
        <div class="text-left">
          <p class="mb-2 text-orange-600 font-bold">⚠️ คำเตือน: การดำเนินการนี้จะตั้งสต็อกสินค้าทั้งหมดเป็น 0!</p>
          <p class="text-sm text-gray-600">กรุณาพิมพ์ <strong class="text-orange-600">RESET</strong> เพื่อยืนยัน</p>
        </div>
        <input id="confirm-text" class="swal2-input" placeholder="พิมพ์ RESET">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ea580c',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'เคลียร์สต็อก',
      cancelButtonText: 'ยกเลิก',
      preConfirm: () => {
        const value = document.getElementById('confirm-text').value;
        if (value !== 'RESET') {
          Swal.showValidationMessage('กรุณาพิมพ์ RESET เพื่อยืนยัน');
          return false;
        }
        return true;
      }
    });

    if (result.isConfirmed) {
      try {
        await settingsAPI.resetStock();
        Swal.fire('สำเร็จ!', 'เคลียร์สต็อกทั้งหมดเป็น 0 เรียบร้อยแล้ว', 'success');
      } catch (error) {
        Swal.fire('ผิดพลาด!', error.response?.data?.error || 'เกิดข้อผิดพลาด', 'error');
      }
    }
  };

  const handleDeleteProducts = async () => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบสินค้าและหมวดหมู่',
      html: `
        <div class="text-left">
          <p class="mb-2 text-red-600 font-bold">⚠️ คำเตือน: การดำเนินการนี้ไม่สามารถย้อนกลับได้!</p>
          <p class="mb-2">ข้อมูลที่จะถูกลบ:</p>
          <ul class="list-disc ml-6 text-sm">
            <li>สินค้าทั้งหมด</li>
            <li>หมวดหมู่ทั้งหมด</li>
            <li>ข้อมูลที่เกี่ยวข้องทั้งหมด</li>
          </ul>
          <p class="mt-3 text-sm text-gray-600">กรุณาพิมพ์ <strong class="text-red-600">DELETE</strong> เพื่อยืนยัน</p>
        </div>
        <input id="confirm-text" class="swal2-input" placeholder="พิมพ์ DELETE">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ลบข้อมูล',
      cancelButtonText: 'ยกเลิก',
      preConfirm: () => {
        const value = document.getElementById('confirm-text').value;
        if (value !== 'DELETE') {
          Swal.showValidationMessage('กรุณาพิมพ์ DELETE เพื่อยืนยัน');
          return false;
        }
        return true;
      }
    });

    if (result.isConfirmed) {
      try {
        await settingsAPI.deleteProducts();
        Swal.fire('สำเร็จ!', 'ลบสินค้าและหมวดหมู่ทั้งหมดเรียบร้อยแล้ว', 'success');
      } catch (error) {
        Swal.fire('ผิดพลาด!', error.response?.data?.error || 'เกิดข้อผิดพลาด', 'error');
      }
    }
  };

  const handleDeletePromotions = async () => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบโปรโมชั่น',
      html: `
        <div class="text-left">
          <p class="mb-2 text-red-600 font-bold">⚠️ คำเตือน: จะลบโปรโมชั่นทั้งหมด!</p>
          <p class="text-sm text-gray-600">กรุณาพิมพ์ <strong class="text-red-600">DELETE</strong> เพื่อยืนยัน</p>
        </div>
        <input id="confirm-text" class="swal2-input" placeholder="พิมพ์ DELETE">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ลบข้อมูล',
      cancelButtonText: 'ยกเลิก',
      preConfirm: () => {
        const value = document.getElementById('confirm-text').value;
        if (value !== 'DELETE') {
          Swal.showValidationMessage('กรุณาพิมพ์ DELETE เพื่อยืนยัน');
          return false;
        }
        return true;
      }
    });

    if (result.isConfirmed) {
      try {
        await settingsAPI.deletePromotions();
        Swal.fire('สำเร็จ!', 'ลบโปรโมชั่นทั้งหมดเรียบร้อยแล้ว', 'success');
      } catch (error) {
        Swal.fire('ผิดพลาด!', error.response?.data?.error || 'เกิดข้อผิดพลาด', 'error');
      }
    }
  };

  const handleDeleteMembers = async () => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบสมาชิก',
      html: `
        <div class="text-left">
          <p class="mb-2 text-red-600 font-bold">⚠️ คำเตือน: การดำเนินการนี้ไม่สามารถย้อนกลับได้!</p>
          <p class="mb-2">ข้อมูลที่จะถูกลบ:</p>
          <ul class="list-disc ml-6 text-sm">
            <li>สมาชิกทั้งหมด</li>
            <li>ประวัติคะแนน</li>
            <li>ประวัติการแลกของรางวัล</li>
          </ul>
          <p class="mt-3 text-sm text-gray-600">กรุณาพิมพ์ <strong class="text-red-600">DELETE</strong> เพื่อยืนยัน</p>
        </div>
        <input id="confirm-text" class="swal2-input" placeholder="พิมพ์ DELETE">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ลบข้อมูล',
      cancelButtonText: 'ยกเลิก',
      preConfirm: () => {
        const value = document.getElementById('confirm-text').value;
        if (value !== 'DELETE') {
          Swal.showValidationMessage('กรุณาพิมพ์ DELETE เพื่อยืนยัน');
          return false;
        }
        return true;
      }
    });

    if (result.isConfirmed) {
      try {
        await settingsAPI.deleteMembers();
        Swal.fire('สำเร็จ!', 'ลบข้อมูลสมาชิกทั้งหมดเรียบร้อยแล้ว', 'success');
      } catch (error) {
        Swal.fire('ผิดพลาด!', error.response?.data?.error || 'เกิดข้อผิดพลาด', 'error');
      }
    }
  };

  const handleDeleteStockCounts = async () => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบการนับสต็อก',
      html: `
        <div class="text-left">
          <p class="mb-2 text-red-600 font-bold">⚠️ คำเตือน: จะลบข้อมูลการนับสต็อกทั้งหมด!</p>
          <p class="mb-2">ข้อมูลที่จะถูกลบ:</p>
          <ul class="list-disc ml-6 text-sm">
            <li>การนับสต็อกทั้งหมด</li>
            <li>รายการสินค้าในการนับสต็อก</li>
          </ul>
          <p class="mt-3 text-sm text-gray-600">กรุณาพิมพ์ <strong class="text-red-600">DELETE</strong> เพื่อยืนยัน</p>
        </div>
        <input id="confirm-text" class="swal2-input" placeholder="พิมพ์ DELETE">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ลบข้อมูล',
      cancelButtonText: 'ยกเลิก',
      preConfirm: () => {
        const value = document.getElementById('confirm-text').value;
        if (value !== 'DELETE') {
          Swal.showValidationMessage('กรุณาพิมพ์ DELETE เพื่อยืนยัน');
          return false;
        }
        return true;
      }
    });

    if (result.isConfirmed) {
      try {
        await settingsAPI.deleteStockCounts();
        Swal.fire('สำเร็จ!', 'ลบข้อมูลการนับสต็อกทั้งหมดเรียบร้อยแล้ว', 'success');
      } catch (error) {
        Swal.fire('ผิดพลาด!', error.response?.data?.error || 'เกิดข้อผิดพลาด', 'error');
      }
    }
  };

  const handleDeleteStockMovements = async () => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบประวัติสต็อก',
      html: `
        <div class="text-left">
          <p class="mb-2 text-red-600 font-bold">⚠️ คำเตือน: จะลบประวัติการเคลื่อนไหวสต็อกทั้งหมด!</p>
          <p class="text-sm text-gray-600">กรุณาพิมพ์ <strong class="text-red-600">DELETE</strong> เพื่อยืนยัน</p>
        </div>
        <input id="confirm-text" class="swal2-input" placeholder="พิมพ์ DELETE">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ลบข้อมูล',
      cancelButtonText: 'ยกเลิก',
      preConfirm: () => {
        const value = document.getElementById('confirm-text').value;
        if (value !== 'DELETE') {
          Swal.showValidationMessage('กรุณาพิมพ์ DELETE เพื่อยืนยัน');
          return false;
        }
        return true;
      }
    });

    if (result.isConfirmed) {
      try {
        await settingsAPI.deleteStockMovements();
        Swal.fire('สำเร็จ!', 'ลบประวัติการเคลื่อนไหวสต็อกทั้งหมดเรียบร้อยแล้ว', 'success');
      } catch (error) {
        Swal.fire('ผิดพลาด!', error.response?.data?.error || 'เกิดข้อผิดพลาด', 'error');
      }
    }
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-6">ตั้งค่าระบบ</h2>

      {!isAdmin && (
        <div className="mb-6 p-4 rounded bg-yellow-100 border border-yellow-400 text-yellow-700">
          ⚠️ คุณสามารถดูข้อมูลได้เท่านั้น ไม่สามารถแก้ไขได้ (เฉพาะผู้ดูแลระบบเท่านั้น)
        </div>
      )}

      {message.text && (
        <div className={`mb-6 p-4 rounded ${
          message.type === 'success'
            ? 'bg-green-100 border border-green-400 text-green-700'
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Store Information Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-4">ข้อมูลร้านค้า</h3>

          <form onSubmit={handleInfoUpdate}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">ชื่อร้าน *</label>
                <input
                  type="text"
                  value={formData.store_name}
                  onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-primary-500"
                  required
                  disabled={!isAdmin}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">ที่อยู่</label>
                <textarea
                  value={formData.store_address}
                  onChange={(e) => setFormData({ ...formData, store_address: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-primary-500"
                  rows="3"
                  disabled={!isAdmin}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">เบอร์โทรศัพท์</label>
                <input
                  type="text"
                  value={formData.store_phone}
                  disabled={!isAdmin}
                  onChange={(e) => setFormData({ ...formData, store_phone: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">อีเมล</label>
                <input
                  type="email"
                  value={formData.store_email}
                  disabled={!isAdmin}
                  onChange={(e) => setFormData({ ...formData, store_email: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">เลขประจำตัวผู้เสียภาษี</label>
                <input
                  type="text"
                  value={formData.tax_id}
                  disabled={!isAdmin}
                  onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">สโลแกน (แสดงใต้ชื่อร้าน)</label>
                <input
                  type="text"
                  value={formData.slogan}
                  disabled={!isAdmin}
                  onChange={(e) => setFormData({ ...formData, slogan: e.target.value })}
                  placeholder="เพราะเขาเป็นมากกว่าสัตว์เลี้ยง"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">ข้อความท้ายหน้าจอลูกค้า</label>
                <input
                  type="text"
                  value={formData.footer_text}
                  disabled={!isAdmin}
                  onChange={(e) => setFormData({ ...formData, footer_text: e.target.value })}
                  placeholder="ขอบคุณที่เลือกใช้บริการ - อายุ วัณโณ สุขัง พะลัง"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={uploading || !isAdmin}
              className="w-full mt-4 bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 transition-colors"
            >
              {uploading ? 'กำลังบันทึก...' : isAdmin ? 'บันทึกข้อมูลร้าน' : 'ไม่มีสิทธิ์แก้ไข'}
            </button>
          </form>
        </div>

        {/* Logo Upload Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-4">โลโก้ร้านค้า</h3>

          <form onSubmit={handleLogoUpload}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">อัปโหลดโลโก้</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-primary-500"
                disabled={!isAdmin}
              />
              <p className="text-xs text-gray-600 mt-1">
                รองรับไฟล์: JPG, PNG, GIF (ขนาดไม่เกิน 5MB)
              </p>
            </div>

            {logoPreview && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">ตัวอย่าง</label>
                <div className="border rounded p-4 flex justify-center bg-gray-50">
                  <img
                    src={logoPreview}
                    alt="Logo Preview"
                    className="max-w-xs h-32 object-contain"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={uploading || !logoFile || !isAdmin}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 transition-colors"
            >
              {uploading ? 'กำลังอัปโหลด...' : 'อัปโหลดโลโก้'}
            </button>
          </form>
        </div>

        {/* Payment QR Settings Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-4">ตั้งค่า QR Code ชำระเงิน</h3>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-3">เลือกประเภท QR Code</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                disabled={!isAdmin}
                onClick={() => {
                  setPaymentMode('manual');
                  setFormData({...formData, qr_mode: 'manual'});
                }}
                className={`p-6 rounded-lg border-2 transition-all ${
                  paymentMode === 'manual'
                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                    : 'border-gray-300 hover:border-blue-300'
                } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="text-4xl mb-3">📤</div>
                <div className="font-semibold text-base">อัปโหลดรูป QR</div>
                <div className="text-sm text-gray-600 mt-2">ใช้รูป QR ที่มีอยู่ (สแกนเฉยๆ ไม่มียอด)</div>
              </button>
              <button
                type="button"
                disabled={!isAdmin}
                onClick={() => {
                  setPaymentMode('promptpay');
                  setFormData({...formData, qr_mode: 'auto', promptpay_type: 'phone'});
                }}
                className={`p-6 rounded-lg border-2 transition-all ${
                  paymentMode === 'promptpay'
                    ? 'border-green-500 bg-green-50 shadow-lg'
                    : 'border-gray-300 hover:border-green-300'
                } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="text-4xl mb-3">⚡</div>
                <div className="font-semibold text-base">PromptPay</div>
                <div className="text-sm text-gray-600 mt-2">QR พร้อมเพย์พร้อมยอด (ตามที่ซื้อ)</div>
              </button>
            </div>
          </div>

          {/* PromptPay Settings */}
          {paymentMode === 'promptpay' && (
            <form onSubmit={handleInfoUpdate}>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 space-y-4">
                <h4 className="font-semibold text-green-800 flex items-center gap-2">
                  <span>⚡</span> ตั้งค่า PromptPay
                </h4>

                <div>
                  <label className="block text-sm font-medium mb-2">ประเภท</label>
                  <select
                    value={formData.promptpay_type}
                    onChange={(e) => setFormData({...formData, promptpay_type: e.target.value, promptpay_id: ''})}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-green-500"
                    disabled={!isAdmin}
                  >
                    <option value="phone">เบอร์โทรศัพท์</option>
                    <option value="citizen_id">เลขบัตรประชาชน</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {formData.promptpay_type === 'phone' ? 'เบอร์โทรศัพท์ (10 หลัก)' : 'เลขบัตรประชาชน (13 หลัก)'}
                  </label>
                  <input
                    type="text"
                    value={formData.promptpay_id}
                    onChange={(e) => setFormData({...formData, promptpay_id: e.target.value})}
                    placeholder={formData.promptpay_type === 'phone' ? '0812345678' : '1234567890123'}
                    maxLength={formData.promptpay_type === 'phone' ? 10 : 13}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-green-500"
                    disabled={!isAdmin}
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    {formData.promptpay_type === 'phone'
                      ? 'ระบุเบอร์โทรที่ผูกกับพร้อมเพย์ (ไม่ต้องใส่ขีด)'
                      : 'ระบุเลขบัตรประชาชนที่ผูกกับพร้อมเพย์ (ไม่ต้องใส่ขีด)'}
                  </p>
                </div>

                <div className="bg-white p-3 rounded border border-green-300">
                  <p className="text-sm text-green-800">
                    <strong>💡 วิธีใช้งาน PromptPay QR:</strong><br/>
                    1. เลือกประเภท (เบอร์โทร หรือ เลขบัตรประชาชน)<br/>
                    2. กรอกหมายเลขที่ผูกกับพร้อมเพย์<br/>
                    3. กดบันทึก<br/>
                    4. ระบบจะสร้าง QR พร้อมเพย์พร้อมยอดอัตโนมัติ
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={uploading || !isAdmin}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 transition-colors"
                >
                  {uploading ? 'กำลังบันทึก...' : isAdmin ? 'บันทึกการตั้งค่า PromptPay' : 'ไม่มีสิทธิ์แก้ไข'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* QR Code Upload Section */}
        {paymentMode === 'manual' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-4">อัปโหลดรูป QR Code (โหมดอัปโหลดเอง)</h3>

          <form onSubmit={handleQrCodeUpload}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">อัปโหลดรูป QR Code</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleQrCodeChange}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-primary-500"
                disabled={!isAdmin}
              />
              <p className="text-xs text-gray-600 mt-1">
                รองรับไฟล์: JPG, PNG, GIF (ขนาดไม่เกิน 5MB)
              </p>
            </div>

            {qrCodePreview && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">ตัวอย่าง</label>
                <div className="border rounded p-4 flex justify-center bg-gray-50">
                  <img
                    src={qrCodePreview}
                    alt="QR Code Preview"
                    className="max-w-xs h-auto object-contain"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={uploading || !qrCodeFile || !isAdmin}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 transition-colors"
            >
              {uploading ? 'กำลังอัปโหลด...' : 'อัปโหลด QR Code'}
            </button>
          </form>
        </div>
        )}

        {/* Member Promotion Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-4">โปรโมชั่นสมาชิก</h3>

          <form onSubmit={handleInfoUpdate}>
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded p-4">
                <h4 className="font-semibold text-purple-800 mb-3">การสะสมคะแนน</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">จำนวนบาท</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={formData.points_earn_baht}
                      disabled={!isAdmin}
                  onChange={(e) => setFormData({ ...formData, points_earn_baht: parseInt(e.target.value) || 100 })}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">ได้คะแนน</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.points_earn_points}
                      disabled={!isAdmin}
                  onChange={(e) => setFormData({ ...formData, points_earn_points: parseFloat(e.target.value) || 0 })}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  ปัจจุบัน: ซื้อ {formData.points_earn_baht} บาท = {formData.points_earn_points} คะแนน
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                <h4 className="font-semibold text-yellow-800 mb-3">การแลกคะแนน</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">จำนวนคะแนน</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={formData.redemption_points}
                      disabled={!isAdmin}
                  onChange={(e) => setFormData({ ...formData, redemption_points: parseInt(e.target.value) || 100 })}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">แลกได้ (บาท)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.redemption_value}
                      disabled={!isAdmin}
                  onChange={(e) => setFormData({ ...formData, redemption_value: parseFloat(e.target.value) || 0 })}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  ปัจจุบัน: {formData.redemption_points} คะแนน = {formData.redemption_value} บาท
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
                <strong>ตัวอย่าง:</strong>
                <p className="mt-1">• ลูกค้าซื้อ 1,000 บาท จะได้ {((1000 / formData.points_earn_baht) * formData.points_earn_points).toFixed(2)} คะแนน</p>
                <p>• แลก {formData.redemption_points} คะแนน จะได้ส่วนลด {formData.redemption_value} บาท</p>
              </div>
            </div>

            <button
              type="submit"
              disabled={uploading || !isAdmin}
              className="w-full mt-4 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 transition-colors"
            >
              {uploading ? 'กำลังบันทึก...' : isAdmin ? 'บันทึกการตั้งค่า' : 'ไม่มีสิทธิ์แก้ไข'}
            </button>
          </form>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-xl font-bold mb-4 text-blue-800">คำแนะนำ</h3>
          <div className="space-y-3 text-sm text-blue-900">
            <div>
              <strong>โลโก้ร้านค้า:</strong>
              <p className="ml-4">• โลโก้จะแสดงที่หน้าจอลูกค้า</p>
              <p className="ml-4">• แนะนำขนาดไม่เกิน 500x500 พิกเซล</p>
            </div>
            <div>
              <strong>QR Code สำหรับโอนเงิน:</strong>
              <p className="ml-4">• อัปโหลด QR Code พร้อมเพย์หรือบัญชีธนาคาร</p>
              <p className="ml-4">• เมื่อเลือก "โอนเงิน" QR Code จะแสดงที่หน้าจอลูกค้า</p>
              <p className="ml-4">• ลูกค้าสแกนเพื่อชำระเงิน</p>
            </div>
            <p className="mt-4 text-blue-700">
              <strong>หมายเหตุ:</strong> ควรใช้รูปภาพที่มีความชัดเจนเพื่อให้แสดงผลได้ดี
            </p>
          </div>
        </div>

        {/* Data Management Section */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6 border-2 border-red-200">
          <h3 className="text-xl font-bold mb-4 text-red-800">⚠️ จัดการข้อมูล (อันตราย)</h3>
          <p className="text-sm text-gray-600 mb-4">
            การดำเนินการเหล่านี้จะลบข้อมูลอย่างถาวร กรุณาใช้ความระมัดระวัง!
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Delete Sales */}
            <div className="border border-red-300 rounded p-4 hover:bg-red-50 transition">
              <h4 className="font-semibold text-red-700 mb-2">🗑️ ลบข้อมูลการขาย</h4>
              <p className="text-xs text-gray-600 mb-3">ลบประวัติการขายทั้งหมด</p>
              <button
                onClick={handleDeleteSales}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors"
              >
                ลบข้อมูลการขาย
              </button>
            </div>

            {/* Reset Stock */}
            <div className="border border-orange-300 rounded p-4 hover:bg-orange-50 transition">
              <h4 className="font-semibold text-orange-700 mb-2">🔄 เคลียร์สต็อก</h4>
              <p className="text-xs text-gray-600 mb-3">ตั้งสต็อกสินค้าทั้งหมดเป็น 0</p>
              <button
                onClick={handleResetStock}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors"
              >
                เคลียร์สต็อก
              </button>
            </div>

            {/* Delete Products */}
            <div className="border border-red-300 rounded p-4 hover:bg-red-50 transition">
              <h4 className="font-semibold text-red-700 mb-2">🗑️ ลบสินค้าและหมวดหมู่</h4>
              <p className="text-xs text-gray-600 mb-3">ลบสินค้าและหมวดหมู่ทั้งหมด</p>
              <button
                onClick={handleDeleteProducts}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors"
              >
                ลบสินค้าทั้งหมด
              </button>
            </div>

            {/* Delete Promotions */}
            <div className="border border-red-300 rounded p-4 hover:bg-red-50 transition">
              <h4 className="font-semibold text-red-700 mb-2">🗑️ ลบโปรโมชั่น</h4>
              <p className="text-xs text-gray-600 mb-3">ลบโปรโมชั่นทั้งหมด</p>
              <button
                onClick={handleDeletePromotions}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors"
              >
                ลบโปรโมชั่นทั้งหมด
              </button>
            </div>

            {/* Delete Members */}
            <div className="border border-red-300 rounded p-4 hover:bg-red-50 transition">
              <h4 className="font-semibold text-red-700 mb-2">🗑️ ลบสมาชิก</h4>
              <p className="text-xs text-gray-600 mb-3">ลบข้อมูลสมาชิกทั้งหมด</p>
              <button
                onClick={handleDeleteMembers}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors"
              >
                ลบสมาชิกทั้งหมด
              </button>
            </div>

            {/* Delete Stock Counts */}
            <div className="border border-red-300 rounded p-4 hover:bg-red-50 transition">
              <h4 className="font-semibold text-red-700 mb-2">🗑️ ลบการนับสต็อก</h4>
              <p className="text-xs text-gray-600 mb-3">ลบข้อมูลการนับสต็อกทั้งหมด</p>
              <button
                onClick={handleDeleteStockCounts}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors"
              >
                ลบการนับสต็อก
              </button>
            </div>

            {/* Delete Stock Movements */}
            <div className="border border-red-300 rounded p-4 hover:bg-red-50 transition">
              <h4 className="font-semibold text-red-700 mb-2">🗑️ ลบประวัติสต็อก</h4>
              <p className="text-xs text-gray-600 mb-3">ลบประวัติการเคลื่อนไหวสต็อก</p>
              <button
                onClick={handleDeleteStockMovements}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors"
              >
                ลบประวัติสต็อก
              </button>
            </div>
          </div>

          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-xs text-yellow-800">
              <strong>⚠️ คำเตือน:</strong> ปุ่มเหล่านี้จะลบข้อมูลอย่างถาวรและไม่สามารถกู้คืนได้
              กรุณาสำรองข้อมูลก่อนดำเนินการ
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
